import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import UserService from "../services/user.service";

function placeholderGradient(name) {
  let h = 0;
  const str = String(name || "MYN");
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  const hue1 = h % 360;
  const hue2 = (hue1 + 45) % 360;
  return {
    from: `hsl(${hue1}, 65%, 45%)`,
    to: `hsl(${hue2}, 70%, 30%)`,
  };
}

function placeholderInitials(name) {
  const src = (String(name || "").trim() || "MYN").replace(/[^\p{L}\p{N}]/gu, "");
  const ch = Array.from(src).slice(0, 2).join("").toUpperCase();
  return ch || "M";
}

const paintWatermark = (canvas) => {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const size = canvas.width;
  const box = Math.round(size * 0.28);
  const x = (size - box) / 2;
  const y = (size - box) / 2;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x, y, box, box);
  ctx.strokeStyle = "#1b4f9c";
  ctx.lineWidth = Math.max(2, Math.round(box * 0.08));
  ctx.strokeRect(x, y, box, box);
  ctx.fillStyle = "#1b4f9c";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `bold ${Math.round(box * 0.32)}px sans-serif`;
  ctx.fillText("MYN", x + box / 2, y + box / 2);
};

const BusinessCardDetail = () => {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    UserService.getPublicBusinessDetail(code)
      .then((res) => {
        if (!active) return;
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError("Dieses Unternehmen oder die Visitenkarte konnte nicht gefunden werden.");
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [code]);

  useEffect(() => {
    if (data && qrRef.current) {
      const timer = window.setTimeout(() => paintWatermark(qrRef.current), 50);
      return () => window.clearTimeout(timer);
    }
  }, [data]);

  const handleCopyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    }
  };

  const cleanPhone = data?.contact_phone ? data.contact_phone.replace(/[^0-9+]/g, "") : "";
  const cleanWhatsapp = data?.contact_whatsapp ? data.contact_whatsapp.replace(/[^0-9]/g, "") : "";
  const waText = `Hallo ${data?.company_name || ""}, ich habe Ihr Profil über MYN.CLUB gefunden!`;

  if (loading) {
    return (
      <div className="container py-5 text-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Wird geladen …</span>
        </div>
        <p className="mt-3 text-muted">Visitenkarte & Flyer werden geladen …</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container py-5" style={{ maxWidth: 640 }}>
        <div className="alert alert-warning text-center p-4 rounded-4 shadow-sm">
          <h2 className="h4 mb-2">Visitenkarte nicht gefunden</h2>
          <p className="mb-4 text-muted">{error || "Der angeforderte Eintrag ist leider nicht verfügbar."}</p>
          <Link to="/" className="btn btn-primary" style={{ minHeight: 44, padding: "10px 24px" }}>
            ← Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  const { from, to } = placeholderGradient(data.company_name);
  const initials = placeholderInitials(data.company_name);
  // Ziel für den Klick auf den QR-Code: Bevorzugt die direkte Weiterleitungs-URL (redeem_url),
  // hilfsweise das direkte Google-Bewertungsformular über die Google Place-ID.
  const googleReviewDirectUrl =
    data.place_id && data.place_id.startsWith("ChIJ")
      ? `https://search.google.com/local/writereview?placeid=${encodeURIComponent(data.place_id)}`
      : null;
  const qrTargetUrl = data.redeem_url || googleReviewDirectUrl || window.location.href;

  return (
    <div className="myn-visitenkarte-page py-4 py-md-5" style={{ background: "#f4f7fb", minHeight: "100vh" }}>
      <div className="container" style={{ maxWidth: 680 }}>
        
        {/* Navigation zurück */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Link
            to="/"
            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
            style={{ minHeight: 44, padding: "8px 16px" }}
          >
            ← Zurück zur Startseite
          </Link>
          <span className="badge bg-primary-subtle text-primary fw-semibold px-3 py-2 rounded-pill">
            Verifizierter Werbeplatz
          </span>
        </div>

        {/* Hauptkarte / Visitenkarte */}
        <div className="card border-0 shadow-lg rounded-4 overflow-hidden bg-white mb-4">
          
          {/* Flyer oder Gradient-Banner */}
          {data.flyer_image_url ? (
            <div className="position-relative bg-dark" style={{ cursor: "pointer" }} onClick={() => setShowImageModal(true)} title="Klicken für Vollbildansicht">
              <img
                src={data.flyer_image_url}
                alt={`Werbeflyer von ${data.company_name}`}
                className="w-100 object-fit-cover"
                style={{ maxHeight: 380, objectFit: "cover", display: "block" }}
              />
              <span
                className="position-absolute bottom-0 end-0 m-3 badge bg-dark bg-opacity-75 text-white px-3 py-2 rounded-pill shadow"
                style={{ fontSize: 12 }}
              >
                🔍 Flyer vergrößern
              </span>
            </div>
          ) : (
            <div
              className="d-flex flex-column justify-content-center align-items-center text-white p-5"
              style={{
                background: `linear-gradient(135deg, ${from}, ${to})`,
                minHeight: 200,
              }}
            >
              <div
                className="fw-bold display-4 mb-2 shadow-sm rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: 90,
                  height: 90,
                  background: "rgba(255,255,255,0.2)",
                  border: "2px solid rgba(255,255,255,0.4)",
                }}
              >
                {initials}
              </div>
              <span className="text-white-50 small">GOOGLE Review Werbepartner</span>
            </div>
          )}

          {/* Inhalt der Visitenkarte */}
          <div className="card-body p-4 p-md-5">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
              <h1 className="h2 fw-bold text-dark mb-0">{data.company_name}</h1>
              {data.service_name && (
                <span className="badge bg-primary text-white px-2 py-1 small rounded">
                  {data.service_name}
                </span>
              )}
            </div>

            {data.promo_title && (
              <h2 className="h5 text-primary fw-semibold mb-3">{data.promo_title}</h2>
            )}

            {data.company_address && (
              <p className="text-muted mb-4 d-flex align-items-center gap-2">
                <span aria-hidden="true">📍</span>
                <span>{data.company_address}</span>
              </p>
            )}

            {data.description && (
              <div className="bg-light p-3 p-md-4 rounded-3 mb-4 border">
                <p className="mb-0 text-dark" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {data.description}
                </p>
              </div>
            )}

            {/* Haupt-Aktionsbutton: Google Bewertung abgeben */}
            {data.redeem_url && (
              <div className="mb-4">
                <a
                  href={data.redeem_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-warning btn-lg w-100 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                  style={{ minHeight: 54, fontSize: 18 }}
                  title={`Bewerten Sie ${data.company_name} direkt bei Google`}
                >
                  <span aria-hidden="true">⭐</span>
                  Jetzt bei Google bewerten
                </a>
              </div>
            )}

            {/* Schnelle Kontakt-Buttons (Anrufen, WhatsApp, Website) */}
            <div className="d-grid gap-2 d-sm-flex justify-content-sm-stretch mb-4">
              {data.contact_phone && (
                <a
                  href={`tel:${cleanPhone}`}
                  className="btn btn-outline-primary flex-fill d-inline-flex align-items-center justify-content-center gap-2"
                  style={{ minHeight: 48 }}
                >
                  <span aria-hidden="true">📞</span>
                  Anrufen
                </a>
              )}

              {data.contact_whatsapp && (
                <a
                  href={`https://wa.me/${cleanWhatsapp}?text=${encodeURIComponent(waText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-success flex-fill d-inline-flex align-items-center justify-content-center gap-2"
                  style={{ minHeight: 48 }}
                >
                  <span aria-hidden="true">💬</span>
                  WhatsApp
                </a>
              )}

              {data.website_url && (
                <a
                  href={data.website_url.startsWith("http") ? data.website_url : `https://${data.website_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-dark flex-fill d-inline-flex align-items-center justify-content-center gap-2"
                  style={{ minHeight: 48 }}
                >
                  <span aria-hidden="true">🌐</span>
                  Website
                </a>
              )}
            </div>

            <hr className="my-4 text-muted" />

            {/* Interaktiver QR-Code mit Aufrufzähler */}
            <div className="row align-items-center g-3">
              <div className="col-12 col-sm-auto text-center">
                <a
                  href={qrTargetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border rounded-3 bg-light d-inline-block shadow-sm text-decoration-none position-relative group"
                  style={{
                    cursor: "pointer",
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  }}
                  title={`Klicken, um die Google-Bewertungsseite von ${data.company_name} direkt zu öffnen`}
                  aria-label={`QR-Code anklicken, um die Google-Bewertungsseite von ${data.company_name} direkt zu öffnen`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.03)";
                    e.currentTarget.style.boxShadow = "0 0.5rem 1rem rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <QRCodeCanvas
                    ref={qrRef}
                    value={data.redeem_url || qrTargetUrl}
                    size={130}
                    level="H"
                    includeMargin
                    aria-label={`QR Code für ${data.company_name}`}
                  />
                  <span
                    className="d-block text-primary small fw-semibold mt-1"
                    style={{ fontSize: "11px" }}
                  >
                    Klicken zum Öffnen ↗
                  </span>
                </a>
              </div>

              <div className="col-12 col-sm">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge bg-light text-dark border px-3 py-2 d-inline-flex align-items-center gap-2">
                    <span aria-hidden="true">👁️</span>
                    <strong>{data.scan_count || 1}</strong> Klicks &amp; Scans
                  </span>
                </div>
                <p className="small text-muted mb-3">
                  Scannen Sie diesen Code mit Ihrer Smartphone-Kamera oder klicken Sie direkt darauf, um zum Google-Bewertungsformular zu gelangen.
                </p>

                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                    style={{ minHeight: 40 }}
                  >
                    🔗 {copied ? "Link kopiert!" : "Link teilen"}
                  </button>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`${data.company_name} – Jetzt bewerten: ${window.location.href}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                    style={{ minHeight: 40 }}
                  >
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Fußzeile mit MYN.CLUB Branding */}
        <p className="text-center text-muted small">
          Powered by <Link to="/" className="text-primary fw-semibold text-decoration-none">MYN.CLUB</Link> · Google Review &amp; Werbeplatz
        </p>
      </div>

      {/* Großansicht-Modal für den Werbeflyer */}
      {showImageModal && data.flyer_image_url && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.85)", zIndex: 1055 }}
          onClick={() => setShowImageModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content bg-transparent border-0">
              <div className="text-end p-2">
                <button
                  type="button"
                  className="btn btn-light rounded-circle shadow"
                  onClick={() => setShowImageModal(false)}
                  style={{ width: 44, height: 44 }}
                  aria-label="Schließen"
                >
                  ✕
                </button>
              </div>
              <div className="modal-body p-0 text-center">
                <img
                  src={data.flyer_image_url}
                  alt={`Großansicht Werbeflyer ${data.company_name}`}
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ maxHeight: "85vh" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCardDetail;
